import React, { useState } from 'react';
import { Insight, ReviewStatus, insightsService } from '../../services/insightsService';
import { getTypeMeta, getPayloadMetrics, REVIEW_STATUS_META, timeAgo } from './insightPresentation';

interface InsightCardProps {
  insight: Insight;
  onReviewed?: (updated: Insight) => void;
}

const REVIEW_ACTIONS: { status: Exclude<ReviewStatus, 'unreviewed'>; label: string; color: string }[] = [
  { status: 'correct', label: 'Correcto', color: '#16a34a' },
  { status: 'incorrect', label: 'Incorrecto', color: '#dc2626' },
  { status: 'not_useful', label: 'No útil', color: '#d97706' },
];

const InsightCard: React.FC<InsightCardProps> = ({ insight, onReviewed }) => {
  const typeMeta = getTypeMeta(insight.type);
  const metrics = getPayloadMetrics(insight);
  const statusMeta = REVIEW_STATUS_META[insight.review_status];

  const [submitting, setSubmitting] = useState<ReviewStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);

  const submitReview = async (status: Exclude<ReviewStatus, 'unreviewed'>) => {
    try {
      setSubmitting(status);
      setError(null);
      const updated = await insightsService.reviewInsight(insight.id, status, note.trim() || undefined);
      onReviewed?.(updated);
      setShowNote(false);
    } catch (err) {
      console.error('Error reviewing insight:', err);
      setError('No se pudo guardar la revisión. Inténtalo de nuevo.');
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        borderLeft: `4px solid ${typeMeta.color}`,
        padding: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
    >
      {/* Header: type + time + status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 16 }}>{typeMeta.emoji}</span>
        <span style={{ fontWeight: 700, color: '#1f2937', fontSize: 14 }}>{typeMeta.label}</span>
        <span style={{ color: '#94a3b8', fontSize: 13 }}>· {timeAgo(insight.created_at)}</span>
        <span
          style={{
            marginLeft: 'auto',
            display: 'inline-block',
            background: statusMeta.bg,
            color: statusMeta.color,
            fontSize: 12,
            fontWeight: 600,
            padding: '3px 10px',
            borderRadius: 12,
            whiteSpace: 'nowrap',
          }}
        >
          {statusMeta.label}
        </span>
      </div>

      {/* Summary sentence (display text) */}
      <div style={{ color: '#1f2937', fontSize: 15, lineHeight: 1.45, marginBottom: metrics.length ? 12 : 0 }}>
        {insight.summary}
      </div>

      {/* Key metrics from payload */}
      {metrics.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {metrics.map((m) => (
            <div
              key={m.label}
              style={{
                background: '#f6f8fa',
                borderRadius: 8,
                padding: '6px 10px',
                fontSize: 13,
              }}
            >
              <span style={{ color: '#64748b' }}>{m.label}: </span>
              <span style={{ color: '#1f2937', fontWeight: 600 }}>{m.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Disclaimer (non-null only for cross_signal) */}
      {insight.disclaimer && (
        <div style={{ background: '#fffbeb', color: '#92400e', fontSize: 12, padding: '8px 10px', borderRadius: 8, marginBottom: 12 }}>
          {insight.disclaimer}
        </div>
      )}

      {/* Existing review note */}
      {insight.review_note && (
        <div style={{ color: '#64748b', fontSize: 13, fontStyle: 'italic', marginBottom: 12 }}>
          Nota: {insight.review_note}
        </div>
      )}

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{error}</div>}

      {/* Review actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {REVIEW_ACTIONS.map((action) => {
          const active = insight.review_status === action.status;
          return (
            <button
              key={action.status}
              onClick={() => submitReview(action.status)}
              disabled={submitting != null}
              style={{
                background: active ? action.color : 'transparent',
                color: active ? '#fff' : action.color,
                border: `1px solid ${action.color}`,
                borderRadius: 8,
                padding: '6px 12px',
                fontSize: 13,
                fontWeight: 600,
                cursor: submitting != null ? 'default' : 'pointer',
                opacity: submitting != null && submitting !== action.status ? 0.5 : 1,
                transition: 'all 0.15s',
              }}
            >
              {submitting === action.status ? 'Guardando…' : action.label}
            </button>
          );
        })}
        <button
          onClick={() => setShowNote((s) => !s)}
          style={{
            background: 'none',
            border: 'none',
            color: '#64748b',
            fontSize: 13,
            cursor: 'pointer',
            marginLeft: 'auto',
          }}
        >
          {showNote ? 'Ocultar nota' : '+ Nota'}
        </button>
      </div>

      {showNote && (
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Nota opcional para esta revisión…"
          rows={2}
          style={{
            width: '100%',
            marginTop: 10,
            padding: 8,
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            fontSize: 13,
            fontFamily: 'inherit',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
        />
      )}
    </div>
  );
};

export default InsightCard;
